import { useNavigate, useParams, Outlet } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useStoreHook } from '../Hooks/StoreHook';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { ORDER_TYPE_DELIVERY_ID, ORDER_TYPE_MENU, ORDER_TYPE_STORE_COUNTER, ORDER_TYPE_STORE_COUNTER_ID } from '../Utils/Constants/Service';
import { isValidViewMode, orderTypeAsSlug, orderTypeAsSlugConvert } from '../Utils/Helpers';
import { useCartContext } from '../Contexts/CartContext';
import { useStoreContext } from '../Contexts/StoreContext';
import { ImSpinner8 } from 'react-icons/im';
import { useCheckoutContext } from '../Contexts/CheckoutContext';
import { useLayoutContext } from '../Contexts/LayoutContext';
import { initialViewMode, VIEW_MODE_LIST } from '../Utils/Constants/Product';
import { useLoginHook } from '../Hooks/LoginHook';
import { useTranslation } from 'react-i18next';
import { TokenManager } from '../Services/TokenManager';
import { ERROR_CART_NOT_FOUND } from '../Utils/Constants/Cart';
import { useAddProductContext } from '../Contexts/AddProductContext';

const AppRouter = () => {

    const { t } = useTranslation();
    const { loadCompanyInfo, loadStoreInfo, getLinkToHome, orderTypeDisabledOnStore, createGuestToken, getActiveCart } = useStoreHook();
    const { orderType, setOrderType, enabledOrderType, getOrderTypeIdByName } = useOrderTypeContext();
    const { setCurrentSwiperSlide, setViewMode, isMobile, company } = useLayoutContext();
    const { setOrder } = useCheckoutContext();
    const { setCartDisplay, setCurrentCart, setCartFinished, setLoadingCurrentCart } = useCartContext();
    const { setTable, setTables, currentStore, currentAddress, setCurrentStore } = useStoreContext();
    const { setRedirectToProductSlug } = useAddProductContext();
    const navigate = useNavigate();
    const { slug, order_type, table_id, slug_product, slug_product_old} = useParams();
    const [loadingRoute, setLoadingRoute] = useState<boolean>(true);
    const [ viewModeByParams, setViewModeByParams ] = useState<string | null>(null);
    const { handleSSO } = useLoginHook();
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();

    useEffect(() => {
        if (isMobile) {
            if (viewModeByParams) {
                setViewMode(viewModeByParams);
            } else {
                if (company) {
                    //* Configuração de exibição de produtos no painel (configurações da marca "Layouts de Exibição > Produtos")
                    setViewMode(company.config.display_layout.products.toLowerCase());
                } else {
                    setViewMode(initialViewMode);
                }
            }
        } else {
            setViewMode(VIEW_MODE_LIST);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, viewModeByParams, company])

    useEffect(() => {
        if (!company) {
            loadCompanyInfo();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!company) return;
        handleSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    const isSSO =
            params.has('state') ||
            params.has('session_state') ||
            params.has('code');

    useEffect(() => {

        //* Verificando Company e SSO antes de navegar
        if (!company || isSSO) return;

        //* Store ID and Table ID from URL
        const storeId = params.get('store_id') || null;
        const getViewMode = params.get('viewMode') || null;
        const storeTableIdByQuery = params.get('store_table_id') ?? table_id ?? null;

        if (getViewMode && isValidViewMode(getViewMode)) {
            setViewModeByParams(getViewMode);
        }

        const handleTableIdFromQuery = () => {
            if (storeTableIdByQuery) {
                //* setTimeout necessário pois a inicialização é null no useStoreContext e está em tempo de execução
                setTimeout(() => {
                    setTable(storeTableIdByQuery);
                }, 0);
            }
        }

        //* Verificando se o serviço está ativo na loja
        const isOrderTypeEnabledOnStore = (order_type: string, services: any[]): boolean => {
            const orderTypeSlug = orderTypeAsSlugConvert(order_type);
            const orderTypeId = getOrderTypeIdByName(orderTypeSlug);

            return services.some(
                (item: any) => item.order_type === orderTypeId && Boolean(item.active)
            );
        };

        interface StoreInfoParams {
            slug_store: string;
            order_type: string;
            slug_product?: string;
        }
        const handleLoadStoreInfo = async ({slug_store, order_type, slug_product}: StoreInfoParams) => {
            const handle_order_type = order_type === ORDER_TYPE_MENU ? ORDER_TYPE_STORE_COUNTER : order_type;
            try {
                const response: any = await loadStoreInfo(slug_store);
                if (response) {

                    if (isOrderTypeEnabledOnStore(handle_order_type, response.store_services)) {
                        setOrderType(orderTypeAsSlugConvert(order_type));

                        if (slug_product) {
                            navigate(`/${orderTypeAsSlug(order_type)}/${response.slug}/produto/${slug_product}`);
                        } else {
                            navigate(`/${orderTypeAsSlug(order_type)}/${response.slug}`);
                        }

                        //* Validando order_type
                        const rawOrderTypeId = getOrderTypeIdByName(orderTypeAsSlugConvert(order_type));

                        const orderTypeId =
                            rawOrderTypeId !== null && rawOrderTypeId !== undefined
                                ? Number(rawOrderTypeId)
                                : null;

                        if (orderTypeId !== null && orderTypeId !== ORDER_TYPE_STORE_COUNTER_ID) {

                            const payload = {
                                store_id: response.id,
                                order_type_id: orderTypeId,
                                order_type: orderTypeAsSlugConvert(order_type)
                            }

                            if (!TokenManager.getToken() && !TokenManager.getGuestToken()) {
                                if ((orderTypeId === ORDER_TYPE_DELIVERY_ID && currentAddress) || (orderTypeId !== ORDER_TYPE_DELIVERY_ID)) {
                                    createGuestToken(response.id, orderTypeId)
                                        .then(() => {
                                            getActiveCart(payload).catch(() => {});
                                        })
                                        .catch(() => {});
                                }
                            } else {
                                getActiveCart(payload).catch((error: any) => {
                                    if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
                                        setLoadingCurrentCart(true);
                                        const orderTypeId = Number(getOrderTypeIdByName(orderType));
                                        createGuestToken(response.id, orderTypeId)
                                            .then(() => {
                                                getActiveCart(payload)
                                                    .catch(() => setLoadingCurrentCart(false));
                                            })
                                        .catch(() => setLoadingCurrentCart(false));
                                    }
                                });
                            }
                        }

                    } else {
                        orderTypeDisabledOnStore();
                        setCurrentStore(null);
                        setOrderType(null);
                        navigate('/');
                    }
                }
            } catch {
                navigate('/');
            } finally {
                setLoadingRoute(false);
            }
        }

        //* Verifica se o orderType está ativo na company
        const isOrderTypeEnabled = (order_type: string): boolean => {
            let orderTypeSlug = orderTypeAsSlugConvert(order_type);
            if (order_type === ORDER_TYPE_MENU) {
                orderTypeSlug = ORDER_TYPE_STORE_COUNTER;
            }

            return enabledOrderType.some(
                (item: any) => item.name === orderTypeSlug && item.active === true
            );
        };

        const handleNavigation = async () => {
            setLoadingRoute(true);

            //* Com store_id fixo na url
            if (storeId) {
                try {
                    const response: any = await loadStoreInfo(storeId);
                    setOrderType(ORDER_TYPE_STORE_COUNTER);
                    handleTableIdFromQuery();
                    if (response?.slug) {
                        navigate(`/${orderTypeAsSlug(ORDER_TYPE_STORE_COUNTER)}/${response.slug}`);
                    }
                } finally {
                    setLoadingRoute(false);
                }

            } else {
                if (order_type && Boolean(orderTypeAsSlugConvert(order_type)) && slug) {
                    handleTableIdFromQuery();

                    if (isOrderTypeEnabled(orderTypeAsSlugConvert(order_type))) {
                        handleLoadStoreInfo({slug_store: slug, order_type: order_type, slug_product});
                    } else {
                        navigate('/');
                    }

                } else {
                    if (slug_product_old) {
                        setRedirectToProductSlug(slug_product_old);
                    }
                    if (currentStore) {
                        // Se tiver product slug armazenado, redireciona para a página do produto
                        if (slug_product_old) {
                            window.location.href = `${getLinkToHome()}/produto/${slug_product_old}`
                        } else {
                            setLoadingRoute(false);
                            navigate(getLinkToHome());
                        }
                    } else {
                        setLoadingRoute(false);
                        navigate('/');
                    }
                }
            }
        }

        if (company && enabledOrderType.length > 0) {
            handleNavigation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, enabledOrderType, currentAddress, TokenManager.getToken()]);

    const previousOrderTypeRef = useRef(orderType);
    useEffect(() => {
        if (orderType && previousOrderTypeRef?.current !== orderType) {
            if (orderType === ORDER_TYPE_STORE_COUNTER) {
                TokenManager.setGuestToken(null);
                setCurrentCart(null);
            } else {
                setTable(null);
                setCartDisplay(null);
                setCartFinished(null);
                setTables(null);
            }
            setOrder(null);
            setCurrentSwiperSlide(0);
            previousOrderTypeRef.current = orderType
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderType]);

    return (
        <main>
            {loadingRoute
                ? <div className='flex items-center justify-center w-full h-dvh text-gray-500'>
                    <div className='flex items-center gap-1'><ImSpinner8 className="animate-spin" /><span>{t('products.main_loading')}</span></div>
                </div>
                : <Outlet />
            }
        </main>
    )
};

export default AppRouter;
